import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import i18n from './messages';
import { useIntl } from 'react-intl-next';
import type { BlockedAppsBannerState } from 'state/persisted/blocked-apps-banner/reducer';
import type { ForgePageName } from 'view/common/forge-ui/types';
import { isBlockedAppsBannerEnabled } from 'feature-flags';
import { FORGE_REQUEST_DETAILS } from 'view/common/forge-ui/constants';
import { token } from '@atlaskit/tokens';
import { getLearnMoreUrl } from 'util/url/url';
import { getLocalStorageItem, setLocalStorageItem } from 'util/localStorage';

interface BlockedAppsBannerProps {
    isAtlassianManagedAccount: boolean;
    projectId: number;
    blockedAppsBannerState?: BlockedAppsBannerState;
    page: ForgePageName;
    accountId: string | undefined;
}

export const learnMoreUrltestId: string = 'sd.portal.blocked.apps.banner.learnMore';
export const doNotShow: string = 'sd.portal.blocked.apps.banner.doNotShow';

const getStyles = (page: ForgePageName) => {
    if (page === FORGE_REQUEST_DETAILS) {
        return {
            marginBottom: `${token('space.300', '24px')}`,
        };
    }

    return {
        marginTop: `${token('space.300', '24px')}`,
    };
};

export const getBlockedAppsBannerCacheKey = (accountId: string, projectId: number): string => {
    return `showBlockedAppsBanner-${projectId}-${accountId}`;
};

const BlockedAppsBannerComponent: React.FC<BlockedAppsBannerProps> = ({
    blockedAppsBannerState,
    page,
    projectId,
    isAtlassianManagedAccount,
    accountId,
}) => {
    const { formatMessage } = useIntl();
    const [showBannerForProject, setShowBannerForProject] = useState(false);
    const doNotShowClickHandler = useCallback(() => {
        if (accountId) {
            setLocalStorageItem(getBlockedAppsBannerCacheKey(accountId, projectId), 'false');
        }
        setShowBannerForProject(false);
    }, [accountId, projectId]);

    const learnMoreUrl = useMemo(() => {
        return getLearnMoreUrl();
    }, []);

    useEffect(() => {
        if (accountId) {
            const showBannerStatus = getLocalStorageItem(getBlockedAppsBannerCacheKey(accountId, projectId));
            if (showBannerStatus && showBannerStatus === 'false') {
                setShowBannerForProject(false);
            } else {
                setShowBannerForProject(true);
            }
        }
    }, [accountId, projectId]);

    if (isBlockedAppsBannerEnabled()) {
        if (
            blockedAppsBannerState &&
            // @ts-ignore
            blockedAppsBannerState[page] &&
            isAtlassianManagedAccount &&
            showBannerForProject
        ) {
            return (
                <div style={getStyles(page)}>
                    <SectionMessage
                        actions={[
                            <SectionMessageAction
                                testId={learnMoreUrltestId}
                                href={learnMoreUrl}
                                key={learnMoreUrltestId}
                            >
                                {formatMessage(i18n.learnMore)}
                            </SectionMessageAction>,
                            <SectionMessageAction testId={doNotShow} onClick={doNotShowClickHandler} key={doNotShow}>
                                {formatMessage(i18n.doNotShow)}
                            </SectionMessageAction>,
                        ]}
                    >
                        <p>{formatMessage(i18n.bannerDescription)}</p>
                    </SectionMessage>
                </div>
            );
        }
    }

    return <></>;
};

export default BlockedAppsBannerComponent;
